import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import EmptyState from "@/components/EmptyState";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useParams } from "@/hooks/useSearchParams";
import type { TableRowMenu } from "@/hooks/useTableRowMenu";
import { useNavigate } from "@/router";
import { MEMBER_ELECTIONS_PAST_PATH } from "../constants";
import { MEMBER_ELECTION_RUN_QUERY } from "../queries";
import type {
  MemberElectionApplicationNode,
  MemberElectionRunApplicationNode,
  MemberElectionRunApplicationsQueryResponse,
  MemberElectionRunApplicationsQueryVariables,
  MembershipElectionRunApplicationStatus
} from "../types";
import { buildMemberElectionPersonPath } from "../_utils/buildMemberElectionPersonPath";
import { buildMemberElectionApplicationColumns } from "./memberElectionApplicationTableColumns";

type PastElectionRunApplicationsPanelProps = {
  electionRunId: string;
  setMenu: (menu: TableRowMenu) => void;
  onViewPerson?: (pgaId: string) => void;
};

function mapRunApplicationToNode(runApplication: MemberElectionRunApplicationNode): MemberElectionApplicationNode {
  const application = runApplication.application;

  return {
    id: application.id,
    profilePhotoUrl: application.profilePhotoUrl,
    email: application.email,
    approvedAt: application.approvedAt,
    electionDate: application.electionDate
      ? {
          id: application.electionDate.id,
          electionDate: application.electionDate.electionDate
        }
      : null,
    electionMemberClassification: application.electionMemberClassification,
    person: {
      pgaId: application.person.pgaId,
      displayName: application.person.displayName,
      primarySection: application.person.primarySection,
      primaryFacility: application.person.primaryFacility
    }
  };
}

const ELECTED_STATUS: MembershipElectionRunApplicationStatus = "SUCCEEDED";

export default function PastElectionRunApplicationsPanel({
  electionRunId,
  setMenu,
  onViewPerson
}: PastElectionRunApplicationsPanelProps) {
  const { isCompactLayout } = useIsCompactLayout();
  const navigate = useNavigate();
  const { searchParamsUrl } = useParams();
  const [loadElectionRun, { data, loading, error, called }] = useLazyQuery<
    MemberElectionRunApplicationsQueryResponse,
    MemberElectionRunApplicationsQueryVariables
  >(MEMBER_ELECTION_RUN_QUERY, {
    fetchPolicy: "cache-first"
  });

  useEffect(() => {
    void loadElectionRun({ variables: { id: electionRunId } });
  }, [electionRunId, loadElectionRun]);

  const electedApplications = useMemo(() => {
    const runApplications = data?.membershipElectionRun?.runApplications ?? [];
    return runApplications
      .filter((runApplication) => runApplication.status === ELECTED_STATUS)
      .map(mapRunApplicationToNode);
  }, [data]);

  const columns = useMemo(
    () =>
      buildMemberElectionApplicationColumns({
        isCompactLayout,
        setMenu,
        onViewPerson,
        onNavigateToPerson: (pgaId) => {
          const personPath = buildMemberElectionPersonPath(
            pgaId,
            searchParamsUrl,
            isCompactLayout,
            MEMBER_ELECTIONS_PAST_PATH
          );
          if (personPath) {
            navigate(personPath);
          }
        }
      }),
    [isCompactLayout, navigate, onViewPerson, searchParamsUrl, setMenu]
  );

  const showLoading = !called || loading;
  const showLoadError = called && !loading && Boolean(error);
  const panelTitle = showLoading ? "Elected Members" : `Elected Members (${electedApplications.length})`;

  if (showLoadError) {
    return (
      <div className="border-t border-sys-brand-surface-container-highest bg-sys-brand-surface-container">
        <div className="px-12 py-8">
          <EmptyState
            iconName="error"
            title="Unable to load elected members"
            caption="Try expanding the row again or refresh the page."
          />
        </div>
      </div>
    );
  }

  return (
    <TableDetailsPanel
      title={panelTitle}
      loading={showLoading}
      data={electedApplications}
      columns={columns}
      renderNoDataEmptyState={() => (
        <EmptyState
          iconName="how_to_vote"
          title="No elected members in this run"
          caption="Only successfully elected applications appear here."
        />
      )}
    />
  );
}
